import React from 'react';
import { Group, Text, Menu, Avatar, Badge, ActionIcon, Box, UnstyledButton, Image, Burger } from '@mantine/core';
import {
  IconLogout,
  IconSettings,
  IconUser,
  IconChevronDown,
  IconBell
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, useIsAdmin } from '../../../application/store/authStore';
import { CUSTOMER_ROUTES, ADMIN_ROUTES } from '../../routing/routes';
import LogoSvg from '../../../assets/images/logo.svg';

export interface HeaderProps {
  /**
   * Callback fired when hamburger menu is clicked (mobile only)
   * Used to toggle the mobile navigation drawer
   */
  onMenuClick?: () => void;
}

/**
 * Header Component
 *
 * Displays:
 * - Hamburger menu button (mobile only, < 768px)
 * - Brand logo/title (Comfortaa font)
 * - User info with role badge
 * - User menu with profile, settings, logout
 * - Notifications icon (placeholder)
 */
export const Header: React.FC<HeaderProps> = ({ onMenuClick }): React.ReactElement => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const isAdmin = useIsAdmin();

  const handleLogout = (): void => {
    void logout().then(() => {
      navigate('/login');
    });
  };

  // SESSION_348: Fix routing - use MY_BUSINESS route for customer profile
  const handleProfile = (): void => {
    navigate(isAdmin ? '/admin/profile' : CUSTOMER_ROUTES.MY_BUSINESS);
  };

  const handleSettings = (): void => {
    navigate(isAdmin ? '/admin/settings' : '/settings');
  };

  const getUserInitials = (): string => {
    if (user?.displayName) {
      const names = user.displayName.split(' ');
      if (names.length >= 2 && names[0] && names[1]) {
        return `${names[0][0]}${names[1][0]}`.toUpperCase();
      }
      return user.displayName.substring(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return '??';
  };

  const getRoleBadgeColor = (): string => {
    return isAdmin ? 'violet' : 'blue';
  };

  return (
    <Group h="100%" px="md" justify="space-between">
      {/* Left side - Mobile burger menu + Brand */}
      <Group gap="xs">
        {/* Mobile hamburger menu (only visible on mobile < 768px) */}
        {onMenuClick && (
          <Burger
            opened={false}
            onClick={onMenuClick}
            hiddenFrom="sm"
            size="sm"
            aria-label="Toggle navigation"
          />
        )}

        {/* Brand Logo/Title */}
        <Image src={LogoSvg} alt="MentionedMe Logo" h={32} w="auto" />
        <Text size="xl" fw={700} ff="Comfortaa, sans-serif" c="brandPrimary">
          MentionedMe
        </Text>
      </Group>

      {/* Right side - Notifications and User Menu */}
      <Group gap="md">
        {/* Notifications Icon (Placeholder) */}
        <ActionIcon variant="subtle" color="gray" size="lg">
          <IconBell size={20} />
        </ActionIcon>

        {/* User Menu */}
        <Menu shadow="md" width={200} position="bottom-end">
          <Menu.Target>
            <UnstyledButton>
              <Group gap="sm">
                <Avatar color={getRoleBadgeColor()} radius="xl">
                  {getUserInitials()}
                </Avatar>
                <Box>
                  <Text size="sm" fw={500}>
                    {user?.displayName ?? user?.email}
                  </Text>
                  <Badge size="xs" color={getRoleBadgeColor()} variant="light">
                    {user?.role}
                  </Badge>
                </Box>
                <IconChevronDown size={14} />
              </Group>
            </UnstyledButton>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Label>Account</Menu.Label>
            <Menu.Item leftSection={<IconUser size={14} />} onClick={handleProfile}>
              Profile
            </Menu.Item>
            <Menu.Item leftSection={<IconSettings size={14} />} onClick={handleSettings}>
              Settings
            </Menu.Item>

            <Menu.Divider />

            <Menu.Item color="red" leftSection={<IconLogout size={14} />} onClick={handleLogout}>
              Logout
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Group>
  );
};
